/**
 * @fileoverview Entry point for the Custom Mosaics Vue application.
 *
 * This module handles:
 * - Conditional mounting based on DOM element existence
 * - Pinia store setup
 * - Reading configuration from data attributes
 *
 * Mount element: #custom-mosaics-app
 *
 * Data attributes read from mount element:
 * - data-app-id: Algolia application ID
 * - data-api-key: Algolia API key
 * - data-posthog-api-key: PostHog API key (optional)
 * - data-index-original: Algolia index for Original Ceramic
 */
import { createApp, h } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { resolvePosthogKey } from '../../shared/utils/resolvePosthogKey.js';
import './main.css';

// Mount element ID
const MOUNT_ELEMENT_ID = 'custom-mosaics-app';

/**
 * Reads configuration from data attributes on the mount element.
 * @param {HTMLElement} el - The mount element
 * @returns {Object} Configuration object with Algolia and PostHog settings
 */
function readConfigFromElement(el) {
    return {
        appId: el.dataset.appId || '',
        apiKey: el.dataset.apiKey || '',
        posthogApiKey: resolvePosthogKey(el.dataset.posthogApiKey),
        indexOriginal: el.dataset.indexOriginal || '',
    };
}

// Check for mount element and initialize the app
const mountElement = document.getElementById(MOUNT_ELEMENT_ID);

if (mountElement) {
    // Read configuration from data attributes
    const config = readConfigFromElement(mountElement);

    // Create Pinia instance
    const pinia = createPinia();

    // Create the Vue app with the root component
    // Pass props from data attributes to the App component
    const app = createApp({
        render() {
            return h(App, {
                appId: config.appId,
                apiKey: config.apiKey,
                posthogApiKey: config.posthogApiKey,
                indexOriginal: config.indexOriginal,
            });
        },
    });

    // Use plugins
    app.use(pinia);

    // Mount the app
    app.mount(mountElement);
}
